import React, { useEffect, useMemo, useRef, useState } from "react";
import { toPng } from "html-to-image";
import { api, API } from "@/lib/api";
import { useTournament } from "@/lib/TournamentContext";
import { useAuth } from "@/lib/AuthContext";
import { toast } from "sonner";
import { Crown, Save, ChevronUp, ChevronDown, Trophy, ChevronLeft, ChevronRight, Check, Download, Lock, RotateCcw, AlertTriangle } from "lucide-react";
import TeamBadge from "@/components/app/TeamBadge";
import TeamPicker from "@/components/app/TeamPicker";
import THIRD_PLACE_LOOKUP from "@/data/thirdPlaceLookup.json";
import BracketGroupResults from "@/components/app/BracketGroupResults";

const GROUPS = ["A","B","C","D","E","F","G","H","I","J","K","L"];

// Official FIFA WC 2026 best-third eligibility per R32 slot, keyed by the winner
// the third faces — shown like fifa.com so unresolved third slots read authentically.
const THIRD_ELIGIBILITY = {
  "3v1E": "3rd A/B/C/D/F",
  "3v1I": "3rd C/D/F/G/H",
  "3v1A": "3rd C/E/F/H/I",
  "3v1L": "3rd E/H/I/J/K",
  "3v1G": "3rd A/E/H/I/J",
  "3v1D": "3rd B/E/F/I/J",
  "3v1B": "3rd E/F/G/I/J",
  "3v1K": "3rd D/E/I/J/L",
};

const codeLabel = (code) => {
  if (!code) return "TBD";
  if (code.startsWith("3v")) return THIRD_ELIGIBILITY[code] || "Best 3rd";
  if (code[0] === "1") return `Winner ${code[1]}`;
  if (code[0] === "2") return `Runner-up ${code[1]}`;
  return code;
};

// R32 bracket cascade — official FIFA World Cup 2026 structure (bracket-TREE order).
// Each entry is [side_a_code, side_b_code]. Codes: "1A"-"1L" = group winners,
// "2A"-"2L" = runners-up, "3v1X" = the best-3rd team that faces winner 1X
// (assigned via the official combination table). Adjacent pairs (0,1),(2,3)...
// meet in the R16; top 8 = left half (→ SF1), bottom 8 = right half (→ SF2).
const R32_MAP = [
  ["1E", "3v1E"],  // M74
  ["1I", "3v1I"],  // M77
  ["2A", "2B"],    // M73
  ["1F", "2C"],    // M75
  ["2K", "2L"],    // M83
  ["1H", "2J"],    // M84
  ["1D", "3v1D"],  // M81
  ["1G", "3v1G"],  // M82
  ["1C", "2F"],    // M76
  ["2E", "2I"],    // M78
  ["1A", "3v1A"],  // M79
  ["1L", "3v1L"],  // M80
  ["1J", "2H"],    // M86
  ["2D", "2G"],    // M88
  ["1B", "3v1B"],  // M85
  ["1K", "3v1K"],  // M87
];

// Which group each best-third team belongs to, then the official combination-table
// assignment: { winnerCode -> third's group letter } for the 8 qualifying groups.
function thirdAssignment(picks) {
  const thirds = picks.best_thirds || [];
  if (thirds.length !== 8) return {};
  const groupOf = {};
  for (const g of GROUPS) {
    const t = picks.groups?.[g]?.[2];
    if (t) groupOf[t] = g;
  }
  const groups = thirds.map((t) => groupOf[t]).filter(Boolean);
  if (groups.length !== 8) return {};
  const key = [...groups].sort().join("");
  return THIRD_PLACE_LOOKUP[key] || {};
}

const ColumnHeader = ({ label, count }) => (
  <div className="label-tag mb-3 text-center h-4">{label} <span className="text-zinc-600">· {count}</span></div>
);

function resolveCode(code, picks) {
  // Returns team_id or null
  if (!code) return null;
  if (code.startsWith("3v")) {
    const grp = thirdAssignment(picks)[code.slice(2)];
    return grp ? (picks.groups?.[grp]?.[2] || null) : null;
  }
  const pos = parseInt(code[0], 10);
  const g = code[1];
  return picks.groups?.[g]?.[pos - 1] || null;
}

function bracketBuild(picks) {
  const r32 = R32_MAP.map(([a, b]) => [resolveCode(a, picks), resolveCode(b, picks)]);
  const r16 = [];
  for (let i = 0; i < 16; i += 2) {
    r16.push([picks.r32_winners?.[i] || null, picks.r32_winners?.[i + 1] || null]);
  }
  const qf = [];
  for (let i = 0; i < 8; i += 2) {
    qf.push([picks.r16_winners?.[i] || null, picks.r16_winners?.[i + 1] || null]);
  }
  const sf = [];
  for (let i = 0; i < 4; i += 2) {
    sf.push([picks.qf_winners?.[i] || null, picks.qf_winners?.[i + 1] || null]);
  }
  const finalPair = [picks.sf_winners?.[0] || null, picks.sf_winners?.[1] || null];
  return { r32, r16, qf, sf, finalPair };
}

const SLOT_H = 56;
const ROUND_W = 200;

function MatchSlot({ a, b, picked, onPick, disabled, teams, code1, code2, testid }) {
  const teamA = teams[a];
  const teamB = teams[b];
  return (
    <div className="w-[200px]" data-testid={testid}>
      {[{ id: a, t: teamA, code: code1 }, { id: b, t: teamB, code: code2 }].map((s, i) => {
        const isPicked = s.id && picked === s.id;
        const isOther  = s.id && picked && picked !== s.id;
        return (
          <button
            key={i}
            disabled={!s.id || disabled}
            data-testid={s.id ? `${testid}-${s.id}` : `${testid}-empty-${i}`}
            onClick={() => s.id && onPick(s.id)}
            className={`w-full flex items-center gap-2 px-2 h-6 border text-xs transition-colors ${
              isPicked ? "bg-primary-accent border-primary-accent text-white"
              : isOther ? "border-zinc-900 text-zinc-600 line-through"
              : s.id   ? "border-zinc-800 bg-black hover:border-primary-accent"
                       : "border-zinc-900 bg-zinc-950 text-zinc-700"
            }`}
          >
            {s.t ? <TeamBadge team={s.t} size="sm" /> :
              <span className="text-[10px] tracking-wide text-zinc-600 truncate">{codeLabel(s.code)}</span>}
          </button>
        );
      })}
    </div>
  );
}

export default function Bracket() {
  const { active } = useTournament();
  const { user } = useAuth();
  const [tab, setTab] = useState("groups"); // groups | thirds | bracket
  const [teams, setTeams] = useState({});
  const [teamsList, setTeamsList] = useState([]);
  const [picks, setPicks] = useState({
    groups: {}, best_thirds: [],
    r32_winners: {}, r16_winners: {}, qf_winners: {}, sf_winners: {},
    champion: null, locked: false, points: 0,
  });
  const [busy, setBusy] = useState(false);
  const [needsRecheck, setNeedsRecheck] = useState(false);
  const [recheckDismissed, setRecheckDismissed] = useState(false);
  const [groupCursor, setGroupCursor] = useState(0); // which group A..L is shown
  const [savedGroups, setSavedGroups] = useState({}); // last-persisted group rankings snapshot

  useEffect(() => {
    if (!active) return;
    (async () => {
      const tid = active.tournament_id;
      const [tR, pR] = await Promise.all([
        api.get(`/tournaments/${tid}/teams`),
        api.get(`/tournaments/${tid}/bracket/me`),
      ]);
      // Dedupe by team_id to avoid duplicate React keys if the sync returns repeats
      const uniqueTeams = Array.from(new Map(tR.data.map((t) => [t.team_id, t])).values());
      setTeamsList(uniqueTeams);
      setTeams(Object.fromEntries(uniqueTeams.map((t) => [t.team_id, t])));
      setPicks({
        groups: pR.data.groups || {},
        best_thirds: pR.data.best_thirds || [],
        r32_winners: pR.data.r32_winners || {},
        r16_winners: pR.data.r16_winners || {},
        qf_winners:  pR.data.qf_winners  || {},
        sf_winners:  pR.data.sf_winners  || {},
        champion: pR.data.champion || null,
        locked: !!pR.data.locked,
        deadline: pR.data.deadline || null,
        points: pR.data.points || 0,
      });
      setSavedGroups(pR.data.groups || {});
      try {
        const rc = await api.get(`/tournaments/${tid}/bracket/recheck-status`);
        setNeedsRecheck(!!rc.data?.needs_recheck);
      } catch (err) { console.warn("recheck-status failed:", err?.message || err); }
    })();
  }, [active]);

  const teamsByGroup = useMemo(() => {
    const m = {};
    teamsList.forEach((t) => {
      if (t.group) (m[t.group] = m[t.group] || []).push(t);
    });
    return m;
  }, [teamsList]);

  const save = async () => {
    setBusy(true);
    try {
      await api.post(`/tournaments/${active.tournament_id}/bracket`, {
        groups: picks.groups,
        best_thirds: picks.best_thirds,
        r32_winners: picks.r32_winners,
        r16_winners: picks.r16_winners,
        qf_winners: picks.qf_winners,
        sf_winners: picks.sf_winners,
        champion: picks.champion,
      });
      setSavedGroups({ ...picks.groups });
      toast.success("Bracket saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const resetBracket = async () => {
    if (!window.confirm("Reset your entire bracket? This clears all group rankings, best thirds, every round and your champion. This can't be undone.")) return;
    setBusy(true);
    try {
      await api.post(`/tournaments/${active.tournament_id}/bracket/reset`);
      setPicks((p) => ({
        groups: {}, best_thirds: [],
        r32_winners: {}, r16_winners: {}, qf_winners: {}, sf_winners: {},
        champion: null, locked: p.locked, deadline: p.deadline, points: 0,
      }));
      setSavedGroups({});
      setGroupCursor(0);
      toast.success("Bracket reset — all picks cleared");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to reset bracket"); }
    finally { setBusy(false); }
  };

  // A group is "saved" when fully ranked AND it matches the last-persisted snapshot
  const groupSaved = (gl) => {
    const arr = picks.groups?.[gl] || [];
    return arr.filter(Boolean).length === 4 && JSON.stringify(arr) === JSON.stringify(savedGroups?.[gl] || []);
  };

  // ---- Group stage rank actions ----
  const setRank = (group, rank /* 0..3 */, team_id) => {
    setPicks((p) => {
      const next = { ...p, groups: { ...p.groups } };
      const arr = [...(next.groups[group] || [null, null, null, null])];
      // remove this team from any other rank in this group
      for (let i = 0; i < arr.length; i++) if (arr[i] === team_id) arr[i] = null;
      arr[rank] = team_id === arr[rank] ? null : team_id;
      next.groups[group] = arr;
      // if a team was demoted out of 3rd, remove from best_thirds
      const thirds = arr.filter((_, i) => i === 2);
      next.best_thirds = (next.best_thirds || []).filter((tid) => {
        // ensure each best-third still has 3rd place in its own group
        for (const gkey of Object.keys(next.groups)) {
          if (next.groups[gkey]?.[2] === tid) return true;
        }
        return false;
      });
      // also clear any downstream knockout pick referencing this team
      const allValid = new Set();
      // collect ALL positions in groups + best_thirds
      Object.values(next.groups).forEach((row) => row.forEach((tid) => tid && allValid.add(tid)));
      next.best_thirds.forEach((tid) => allValid.add(tid));
      const sanitize = (winnersObj, parentSet) => {
        const out = {};
        for (const [k, v] of Object.entries(winnersObj || {})) {
          if (parentSet.has(v)) out[k] = v;
        }
        return out;
      };
      // R32 winners must be in resolved R32 pairs — but we don't have computed pairs here yet, skip strict check
      return next;
    });
  };

  // ---- Third place picks ----
  const thirdsPool = useMemo(
    () => GROUPS.map((g) => ({ group: g, team_id: picks.groups?.[g]?.[2] })).filter((x) => x.team_id),
    [picks.groups]
  );

  // Drop any saved best-third that's no longer a valid 3rd-place pick (e.g. after
  // re-ranking a group, or a team-id change from the data sync). Otherwise a stale
  // id silently occupies a slot, renders as an unremovable "Empty", and blocks
  // picking the 8th team (users get stuck at 7).
  useEffect(() => {
    if (!Object.keys(teams).length) return;
    const validIds = new Set(thirdsPool.map((x) => x.team_id).filter((id) => teams[id]));
    setPicks((p) => {
      const cur = p.best_thirds || [];
      const cleaned = cur.filter((id) => validIds.has(id));
      return cleaned.length === cur.length ? p : { ...p, best_thirds: cleaned };
    });
  }, [thirdsPool, teams]);

  const toggleThird = (team_id) => {
    setPicks((p) => {
      const arr = [...(p.best_thirds || [])];
      const idx = arr.indexOf(team_id);
      if (idx >= 0) arr.splice(idx, 1);
      else if (arr.length < 8) arr.push(team_id);
      else { toast.error("Pick exactly 8"); return p; }
      return { ...p, best_thirds: arr };
    });
  };

  const reorderThird = (idx, dir) => {
    setPicks((p) => {
      const arr = [...(p.best_thirds || [])];
      const swap = idx + dir;
      if (swap < 0 || swap >= arr.length) return p;
      [arr[idx], arr[swap]] = [arr[swap], arr[idx]];
      return { ...p, best_thirds: arr };
    });
  };

  // ---- Bracket pick ----
  const bracketView = useMemo(() => bracketBuild(picks), [picks]);

  const setKnockout = (round, idx, team_id) => {
    setPicks((p) => {
      const key = `${round}_winners`;
      const next = { ...p, [key]: { ...(p[key] || {}) } };
      next[key][idx] = next[key][idx] === team_id ? null : team_id;
      // cascade: invalidate later rounds that depended on this slot
      const order = ["r32", "r16", "qf", "sf"];
      const startIdx = order.indexOf(round);
      for (let i = startIdx + 1; i < order.length; i++) {
        next[`${order[i]}_winners`] = { ...(next[`${order[i]}_winners`] || {}) };
        // Determine which downstream slots reference 'idx'
        // R32 idx -> R16 slot floor(idx/2); R16 -> QF floor(idx/2); etc.
        let nextIdxToCheck = Math.floor(idx / Math.pow(2, i - startIdx));
        // we'd need to recompute the chain; simplest: scrub everything below if champion was the eliminated team
      }
      if (next.champion === team_id) next.champion = null;
      return next;
    });
  };

  const setChampion = (team_id) => setPicks((p) => ({ ...p, champion: p.champion === team_id ? null : team_id }));

  if (!active) return null;
  const cfg = active.config || {};

  // Progress badges
  const groupsDone = Object.values(picks.groups || {}).filter((arr) => (arr || []).filter(Boolean).length === 4).length;
  const thirdsCount = (picks.best_thirds || []).length;

  return (
    <div className="px-3 md:px-8 py-6 md:py-8 grid-bg min-h-[calc(100vh-65px)]">
      <div className="flex items-end justify-between mb-5 flex-wrap gap-3">
        <div>
          <div className="label-tag mb-1">Cascade Predictions</div>
          <h1 className="font-display text-3xl md:text-5xl uppercase">Bracket Challenge</h1>
        </div>
        <div className="flex items-center gap-3">
          <div className="surface-card px-3 py-2"><div className="label-tag">Points</div><div className="font-display text-2xl text-primary-accent">{picks.points}</div></div>
          {!picks.locked && (
            <button onClick={save} disabled={busy} data-testid="save-bracket-btn" className="bg-primary-accent text-white px-4 md:px-6 py-2.5 font-display tracking-widest text-sm md:text-base uppercase hover:bg-[var(--primary-hover)] flex items-center gap-2">
              <Save size={14}/> {busy ? "Saving…" : "Save"}
            </button>
          )}
          {!picks.locked && (
            <button onClick={resetBracket} disabled={busy} data-testid="reset-bracket-btn" className="surface-card px-4 py-2.5 font-display tracking-widest text-sm md:text-base uppercase text-zinc-300 hover:text-primary-accent hover:border-primary-accent flex items-center gap-2">
              <RotateCcw size={14}/> Reset
            </button>
          )}
        </div>
      </div>

      {needsRecheck && !recheckDismissed && !picks.locked && (
        <div className="border border-amber-500/50 bg-amber-500/10 p-3 mb-4 text-sm text-amber-300 flex items-start gap-2" data-testid="bracket-recheck-banner">
          <AlertTriangle size={16} className="flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            We updated the <span className="font-semibold">Round of 32</span> to the official FIFA schedule, so some matchups moved. Please review your knockout picks and re-save before the bracket locks.
          </div>
          <button data-testid="recheck-dismiss" onClick={() => setRecheckDismissed(true)} className="text-amber-400/70 hover:text-amber-200 flex-shrink-0">✕</button>
        </div>
      )}

      {picks.locked ? (
        <div className="border border-primary-accent/40 bg-primary-accent/5 p-3 mb-4 text-sm text-primary-accent flex items-center gap-2" data-testid="bracket-locked-banner">
          <Lock size={14} /> The bracket is locked — the tournament has kicked off. Your picks can no longer be changed.
        </div>
      ) : picks.deadline ? (
        <div className="border border-zinc-800 bg-black/40 p-3 mb-4 text-xs uppercase tracking-widest text-zinc-400 flex items-center gap-2" data-testid="bracket-deadline-banner">
          <Lock size={12} /> Locks at first kickoff · {new Date(picks.deadline).toLocaleString([], { dateStyle: "medium", timeStyle: "short" })}
        </div>
      ) : null}


      <div className="flex flex-wrap gap-1 mb-4 surface-card p-1 inline-flex" data-testid="bracket-tabs">
        <button onClick={() => setTab("groups")} data-testid="tab-groups"
          className={`px-3 py-1.5 text-xs uppercase tracking-widest flex items-center gap-2 ${tab === "groups" ? "bg-primary-accent text-white" : "text-zinc-400"}`}>
          1 · Groups <span className="text-[10px] opacity-70">{groupsDone}/12</span>
        </button>
        <button onClick={() => setTab("thirds")} data-testid="tab-thirds"
          className={`px-3 py-1.5 text-xs uppercase tracking-widest flex items-center gap-2 ${tab === "thirds" ? "bg-primary-accent text-white" : "text-zinc-400"}`}>
          2 · Best 3rds <span className="text-[10px] opacity-70">{thirdsCount}/8</span>
        </button>
        <button onClick={() => setTab("bracket")} data-testid="tab-bracket"
          className={`px-3 py-1.5 text-xs uppercase tracking-widest ${tab === "bracket" ? "bg-primary-accent text-white" : "text-zinc-400"}`}>
          3 · Knockouts
        </button>
      </div>

      <div className="text-xs text-zinc-400 mb-4 flex flex-wrap items-center gap-x-3 gap-y-1">
        <span>Group ranks: <span className="text-white">1st +{cfg.pts_group_first}</span> · <span className="text-white">2nd +{cfg.pts_group_second}</span> · <span className="text-white">3rd +{cfg.pts_group_third}</span> · <span className="text-white">4th +{cfg.pts_group_fourth}</span> · <span className="text-white">Best 3rd advancing +{cfg.pts_best_third}</span></span>
        <span>· Knockouts: R32 {cfg.pts_r32} · R16 {cfg.pts_r16} · QF {cfg.pts_qf} · SF {cfg.pts_sf} · Final {cfg.pts_final}</span>
      </div>

      {/* TAB 1 — GROUPS (read-only results when locked, editable picker otherwise) */}
      {tab === "groups" && picks.locked && user?.user_id && (
        <div className="surface-card p-4 md:p-6" data-testid="bracket-group-results-card">
          <div className="label-tag mb-1">Step 1 — Group Stage Results</div>
          <p className="text-xs text-zinc-400 mb-4">The bracket is locked. Here&apos;s how your group predictions stack up against the actual standings — completed groups are final, in-progress groups update as matches settle.</p>
          <BracketGroupResults tournamentId={active.tournament_id} userId={user.user_id} showHeader={false} section="groups" showShare tournamentName={active.name} />
        </div>
      )}
      {tab === "groups" && !picks.locked && (() => {
        const g = GROUPS[groupCursor];
        const groupTeams = teamsByGroup[g] || [];
        const values = picks.groups?.[g] || [null, null, null, null];
        const setValues = (next) => setPicks((p) => ({
          ...p,
          groups: { ...p.groups, [g]: next.slice(0, 4) },
        }));
        const done = values.filter(Boolean).length === 4;
        const isLast = groupCursor === GROUPS.length - 1;
        return (
          <div className="surface-card p-4 md:p-6">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <div>
                <div className="label-tag">Step 1 — Group {g} ({groupCursor + 1} of {GROUPS.length})</div>
                <div className="flex items-center gap-3 flex-wrap">
                  <h2 className="font-display text-3xl uppercase">Group {g}</h2>
                  <span
                    data-testid={`group-lock-indicator-${g}`}
                    className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-widest border ${
                      picks.locked
                        ? "border-primary-accent/50 text-primary-accent bg-primary-accent/10"
                        : "border-green-500/40 text-green-400 bg-green-500/5"
                    }`}
                  >
                    <Lock size={10} /> {picks.locked ? "Locked" : "Open"}
                  </span>
                  {!picks.locked && done && (
                    groupSaved(g) ? (
                      <span data-testid={`group-saved-${g}`} className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-widest border border-green-500/50 text-green-400 bg-green-500/10">
                        <Check size={10} /> Saved
                      </span>
                    ) : (
                      <span data-testid={`group-unsaved-${g}`} className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-widest border border-yellow-400/50 text-yellow-300 bg-yellow-400/5">
                        Unsaved
                      </span>
                    )
                  )}
                </div>
                <p className="text-xs text-zinc-400 mt-1">Click teams in order — 1st → 2nd → 3rd → 4th. Drag the slot panel to reorder.</p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  data-testid="group-prev"
                  disabled={groupCursor === 0}
                  onClick={() => setGroupCursor((c) => Math.max(0, c - 1))}
                  className="border border-zinc-800 text-xs uppercase tracking-widest px-3 py-2 flex items-center gap-1 disabled:opacity-30 hover:border-white/30"
                ><ChevronLeft size={14}/> Prev</button>
                <button
                  data-testid="group-next"
                  disabled={!done}
                  onClick={async () => {
                    if (done && !picks.locked && !groupSaved(g)) await save();
                    if (isLast) setTab("thirds");
                    else setGroupCursor((c) => Math.min(GROUPS.length - 1, c + 1));
                  }}
                  className="bg-primary-accent text-white text-xs uppercase tracking-widest px-4 py-2 flex items-center gap-1 disabled:opacity-30"
                >{isLast ? "Continue to Best 3rds" : "Save & Next"} <ChevronRight size={14}/></button>
              </div>
            </div>

            {/* Group progress strip */}
            <div className="flex flex-wrap gap-1 mb-5">
              {GROUPS.map((gx, i) => {
                const filled = (picks.groups?.[gx] || []).filter(Boolean).length === 4;
                const current = i === groupCursor;
                return (
                  <button
                    key={gx}
                    data-testid={`group-jump-${gx}`}
                    onClick={() => setGroupCursor(i)}
                    className={`relative w-8 h-8 text-xs font-display border flex items-center justify-center ${
                      current ? "bg-primary-accent border-primary-accent text-white"
                      : filled ? "border-green-500/50 text-green-400"
                      : "border-zinc-800 text-zinc-500"
                    }`}
                  >
                    {gx}
                    {groupSaved(gx) && !current && (
                      <Check size={10} className="absolute -top-1 -right-1 text-green-400 bg-black rounded-full" data-testid={`group-jump-saved-${gx}`} />
                    )}
                  </button>
                );
              })}
            </div>

            {groupTeams.length === 0 ? (
              <div className="text-sm text-zinc-500">No teams in group {g} yet — wait for the integration sync.</div>
            ) : (
              <TeamPicker
                pool={groupTeams.map((t) => ({ team: t }))}
                values={values}
                setValues={setValues}
                slotCount={4}
                slotLabels={["1st", "2nd", "3rd", "4th"]}
                disabled={picks.locked}
                testidPrefix={`grp-${g}`}
              />
            )}
          </div>
        );
      })()}

      {/* TAB 2 — BEST THIRDS (read-only results when locked, editable picker otherwise) */}
      {tab === "thirds" && picks.locked && user?.user_id && (
        <div className="surface-card p-4 md:p-6" data-testid="bracket-thirds-results-card">
          <div className="label-tag mb-1">Step 2 — Best 3rds Result</div>
          <p className="text-xs text-zinc-400 mb-4">The bracket is locked. Here are your 8 best-3rd picks against the teams currently qualifying — points lock in once the whole group stage completes.</p>
          <BracketGroupResults tournamentId={active.tournament_id} userId={user.user_id} showHeader={false} section="thirds" showShare tournamentName={active.name} />
        </div>
      )}
      {tab === "thirds" && !picks.locked && (() => {
        const setBestThirds = (next) => setPicks((p) => ({ ...p, best_thirds: next.filter(Boolean).slice(0, 8) }));
        // Pad values to length 8 so empty slots render
        const paddedValues = (() => {
          const arr = [...(picks.best_thirds || [])];
          while (arr.length < 8) arr.push(null);
          return arr.slice(0, 8);
        })();
        return (
          <div className="surface-card p-4 md:p-6">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <div>
                <div className="label-tag">Step 2 — Best 3rd-Placed Teams</div>
                <h2 className="font-display text-3xl uppercase">Pick 8 of 12</h2>
                <p className="text-xs text-zinc-400 mt-1">Click order = T1 → T8 in the bracket. Drag to reorder. The 4 you don&apos;t pick are eliminated.</p>
              </div>
              <button
                data-testid="thirds-done"
                disabled={(picks.best_thirds || []).filter(Boolean).length !== 8}
                onClick={() => setTab("bracket")}
                className="bg-primary-accent text-white text-xs uppercase tracking-widest px-4 py-2 flex items-center gap-1 disabled:opacity-30"
              >Continue to Bracket <ChevronRight size={14}/></button>
            </div>

            {thirdsPool.length < 12 && (
              <div className="border border-yellow-500/40 bg-yellow-500/5 p-3 mb-4 text-sm text-yellow-300">
                You&apos;ve only ranked {thirdsPool.length}/12 groups. Go back to Step 1 to complete them.
              </div>
            )}

            {thirdsPool.length === 0 ? (
              <div className="text-sm text-zinc-500">Predict 3rd place for at least one group first.</div>
            ) : (
              <TeamPicker
                pool={thirdsPool.map(({ team_id, group }) => ({ team: teams[team_id], label: `Group ${group}` })).filter((x) => x.team)}
                values={paddedValues}
                setValues={setBestThirds}
                slotCount={8}
                slotLabels={["T1","T2","T3","T4","T5","T6","T7","T8"]}
                disabled={picks.locked}
                testidPrefix="thirds"
              />
            )}
          </div>
        );
      })()}

      {/* TAB 3 — BRACKET */}
      {tab === "bracket" && (
        <BracketView
          teams={teams}
          picks={picks}
          bracketView={bracketView}
          setKnockout={setKnockout}
          setChampion={setChampion}
        />
      )}
    </div>
  );
}


function BracketView({ teams, picks, bracketView, setKnockout, setChampion }) {
  const { r32, r16, qf, sf, finalPair } = bracketView;
  const exportRef = useRef(null);
  const [downloading, setDownloading] = useState(false);

  const handleDownload = async () => {
    const node = exportRef.current;
    if (!node || downloading) return;
    setDownloading(true);
    const imgs = Array.from(node.querySelectorAll("img"));
    const originals = imgs.map((im) => im.getAttribute("src"));
    try {
      // Swap cross-origin crests for same-origin data URLs to avoid canvas tainting
      await Promise.all(
        imgs.map(async (im, i) => {
          const src = originals[i];
          if (!src || src.startsWith("data:")) return;
          try {
            const res = await fetch(`${API}/crest-proxy?url=${encodeURIComponent(src)}`);
            const blob = await res.blob();
            const dataUrl = await new Promise((resolve, reject) => {
              const fr = new FileReader();
              fr.onload = () => resolve(fr.result);
              fr.onerror = reject;
              fr.readAsDataURL(blob);
            });
            im.setAttribute("src", dataUrl);
          } catch { /* keep original src on failure */ }
        })
      );
      await new Promise((r) => setTimeout(r, 200));
      const dataUrl = await toPng(node, {
        pixelRatio: 2,
        cacheBust: true,
        backgroundColor: "#09090b",
      });
      const link = document.createElement("a");
      link.download = `predict-league-bracket-${Date.now()}.png`;
      link.href = dataUrl;
      link.click();
      toast.success("Bracket image downloaded — share away!");
    } catch (e) {
      toast.error("Couldn't generate the image. Please try again.");
    } finally {
      imgs.forEach((im, i) => { if (originals[i]) im.setAttribute("src", originals[i]); });
      setDownloading(false);
    }
  };

  // Render one half — 8 R32 → 4 R16 → 2 QF → 1 SF winner (finalist)
  const renderHalf = (offset, dir) => {
    const r32Slice = r32.slice(offset, offset + 8);
    const r16Slice = r16.slice(offset / 2, offset / 2 + 4);
    const qfSlice  = qf.slice(offset / 4, offset / 4 + 2);
    const sfSlice  = sf.slice(offset / 8, offset / 8 + 1);

    return (
      <div className={`flex ${dir === "rtl" ? "flex-row-reverse" : "flex-row"} items-stretch gap-2`}>
        {/* R32 */}
        <div className="flex flex-col">
          <ColumnHeader label="R32" count={8}/>
          <div className="flex flex-col justify-around" style={{ height: SLOT_H * 16 }}>
            {r32Slice.map(([a, b], i) => {
              const slotIdx = offset + i;
              return (
                <div key={i} style={{ height: SLOT_H }} className="flex items-center">
                  <MatchSlot a={a} b={b} picked={picks.r32_winners?.[slotIdx]}
                    onPick={(tid) => setKnockout("r32", slotIdx, tid)} disabled={picks.locked}
                    teams={teams} code1={R32_MAP[slotIdx]?.[0]} code2={R32_MAP[slotIdx]?.[1]}
                    testid={`r32-${slotIdx}`}/>
                </div>
              );
            })}
          </div>
        </div>
        {/* R16 */}
        <div className="flex flex-col">
          <ColumnHeader label="R16" count={4}/>
          <div className="flex flex-col justify-around" style={{ height: SLOT_H * 16 }}>
            {r16Slice.map(([a, b], i) => {
              const slotIdx = (offset / 2) + i;
              return (
                <div key={i} style={{ height: SLOT_H * 2 }} className="flex items-center">
                  <MatchSlot a={a} b={b} picked={picks.r16_winners?.[slotIdx]}
                    onPick={(tid) => setKnockout("r16", slotIdx, tid)} disabled={picks.locked}
                    teams={teams} testid={`r16-${slotIdx}`}/>
                </div>
              );
            })}
          </div>
        </div>
        {/* QF */}
        <div className="flex flex-col">
          <ColumnHeader label="QF" count={2}/>
          <div className="flex flex-col justify-around" style={{ height: SLOT_H * 16 }}>
            {qfSlice.map(([a, b], i) => {
              const slotIdx = (offset / 4) + i;
              return (
                <div key={i} style={{ height: SLOT_H * 4 }} className="flex items-center">
                  <MatchSlot a={a} b={b} picked={picks.qf_winners?.[slotIdx]}
                    onPick={(tid) => setKnockout("qf", slotIdx, tid)} disabled={picks.locked}
                    teams={teams} testid={`qf-${slotIdx}`}/>
                </div>
              );
            })}
          </div>
        </div>
        {/* SF */}
        <div className="flex flex-col">
          <ColumnHeader label="SF" count={1}/>
          <div className="flex flex-col justify-around" style={{ height: SLOT_H * 16 }}>
            {sfSlice.map(([a, b], i) => {
              const slotIdx = (offset / 8) + i;
              return (
                <div key={i} style={{ height: SLOT_H * 8 }} className="flex items-center">
                  <MatchSlot a={a} b={b} picked={picks.sf_winners?.[slotIdx]}
                    onPick={(tid) => setKnockout("sf", slotIdx, tid)} disabled={picks.locked}
                    teams={teams} testid={`sf-${slotIdx}`}/>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  const champion = picks.champion ? teams[picks.champion] : null;
  const finalCandidates = [finalPair[0], finalPair[1]].filter(Boolean).map((tid) => teams[tid]).filter(Boolean);

  return (
    <div>
      <div className="flex justify-end mb-3">
        <button
          data-testid="download-bracket-btn"
          onClick={handleDownload}
          disabled={downloading}
          className="border border-zinc-700 hover:border-primary-accent bg-zinc-950 text-white px-4 py-2 font-display tracking-widest text-xs uppercase flex items-center gap-2 disabled:opacity-50 transition-colors"
        >
          <Download size={14} /> {downloading ? "Generating…" : "Download / Share Image"}
        </button>
      </div>
      <div className="overflow-x-auto pb-6">
        <div ref={exportRef} className="w-max bg-zinc-950 grid-bg p-6">
          {/* Branded header (for shared image) */}
          <div className="flex items-center justify-between gap-10 mb-6 pb-4 border-b border-white/10">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-primary-accent flex items-center justify-center">
                <Trophy size={20} />
              </div>
              <div>
                <div className="font-display text-xl tracking-wider leading-none">JINXING SPORTS</div>
                <div className="label-tag mt-1">FIFA World Cup 2026 · My Bracket</div>
              </div>
            </div>
            {champion && (
              <div className="flex items-center gap-3">
                <div className="text-right">
                  <div className="label-tag">Champion</div>
                  <div className="font-display text-lg uppercase text-yellow-400 leading-none mt-1">
                    {champion.shortName || champion.name}
                  </div>
                </div>
                <Crown size={28} className="text-yellow-400" />
              </div>
            )}
          </div>

          <div className="flex items-center justify-center gap-2 min-w-max">
            {/* LEFT HALF (left→right) */}
            {renderHalf(0, "ltr")}

            {/* FINAL center */}
            <div className="flex flex-col items-center justify-center mx-2" style={{ height: SLOT_H * 16 }}>
              <div className="label-tag mb-3 text-center">Final</div>
              <div className="flex items-center gap-3 mb-4">
                {finalCandidates.length === 0 && <div className="text-xs text-zinc-600 uppercase tracking-widest">Pick the SFs</div>}
                {finalCandidates.map((t) => {
                  const isWinner = picks.champion === t.team_id;
                  return (
                    <button key={t.team_id} onClick={() => setChampion(t.team_id)} disabled={picks.locked}
                      data-testid={`champion-${t.team_id}`}
                      className={`p-3 border ${isWinner ? "bg-yellow-400 border-yellow-400 text-black" : "border-zinc-800 hover:border-yellow-400"}`}>
                      <TeamBadge team={t} size="md"/>
                    </button>
                  );
                })}
              </div>
              {champion && (
                <div className="flex flex-col items-center mt-2">
                  <Crown size={28} className="text-yellow-400 mb-1"/>
                  <div className="label-tag mb-1">Your Champion</div>
                  <TeamBadge team={champion} size="lg"/>
                </div>
              )}
            </div>

            {/* RIGHT HALF (right→left) */}
            {renderHalf(8, "rtl")}
          </div>

          {/* Footer watermark (for shared image) */}
          <div className="mt-6 pt-3 border-t border-white/10 text-center text-[11px] text-zinc-500 uppercase tracking-[0.3em]">
            Predict · Survive · Conquer
          </div>
        </div>
      </div>
    </div>
  );
}
